import { Link } from "@tanstack/react-router";
import { Phone, Mail, MapPin, ExternalLink } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { NMServiceArea } from "@/components/NMServiceArea";
import { useBusinessEmail } from "@/lib/email";
import { GoogleIcon } from "@/components/GoogleIcon";
import { pic } from "@/lib/responsive-images";

const logoHorizontal = pic("logo-horizontal");

const GOOGLE_BUSINESS_URL =
  "https://www.google.com/maps?cid=6621967010212129613";

export function SiteFooter() {
  const { t } = useI18n();
  const email = useBusinessEmail();
  return (
    <footer className="bg-primary text-primary-foreground">
      <div className="mx-auto grid max-w-7xl gap-10 px-4 py-16 sm:px-6 md:grid-cols-2 lg:grid-cols-4 lg:px-8">
        {/* Brand */}
        <div className="md:col-span-2 lg:col-span-1">
          <div className="flex items-center gap-4">
            <img
              src={logoHorizontal.src}
              srcSet={logoHorizontal.srcSet}
              sizes="72px"
              alt="AMR 3 Design & Construction logo"
              width={140}
              height={140}
              className="h-16 w-16 rounded-full object-cover sm:h-[72px] sm:w-[72px]"
            />
            <div className="font-serif text-2xl font-semibold text-accent leading-tight">
              AMR 3 Design & Construction
            </div>
          </div>
          <p className="mt-4 max-w-md text-sm text-primary-foreground/75">{t("footer.tagline")}</p>
          <a
            href={GOOGLE_BUSINESS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-6 inline-flex items-center gap-2 rounded-full border border-primary-foreground/20 bg-primary-foreground/5 px-4 py-2 text-sm transition-colors hover:border-accent hover:text-accent"
          >
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-white">
              <GoogleIcon className="h-3.5 w-3.5" />
            </span>
            <span>{t("footer.googleBusiness")}</span> <ExternalLink className="h-3 w-3" />
          </a>
        </div>

        {/* Navigate */}
        <div>
          <div className="mb-4 font-serif text-base text-accent">{t("footer.navigate")}</div>
          <ul className="space-y-2 text-sm">
            <li>
              <Link to="/" className="hover:text-accent">
                {t("nav.home")}
              </Link>
            </li>
            <li>
              <Link to="/services" className="hover:text-accent">
                {t("nav.services")}
              </Link>
            </li>
            <li>
              <Link to="/about" className="hover:text-accent">
                {t("nav.about")}
              </Link>
            </li>
            <li>
              <Link to="/work" className="hover:text-accent">
                {t("nav.work")}
              </Link>
            </li>
            <li>
              <Link to="/faq" className="hover:text-accent">
                {t("nav.faq")}
              </Link>
            </li>
            <li>
              <Link to="/blog" className="hover:text-accent">
                {t("nav.blog")}
              </Link>
            </li>
            <li>
              <Link to="/contact" className="hover:text-accent">
                {t("nav.contact")}
              </Link>
            </li>
          </ul>
        </div>

        {/* Contact */}
        <div>
          <div className="mb-4 font-serif text-base text-accent">{t("nav.contact")}</div>
          <div className="space-y-3 text-sm">
            <a href="tel:+15058680847" className="flex items-center gap-3 hover:text-accent">
              <Phone className="h-4 w-4 shrink-0" />
              <span>1-505-868-0847</span>
            </a>
            <a
              href={email.href}
              className="flex items-center gap-3 hover:text-accent"
            >
              <Mail className="h-4 w-4 shrink-0" />
              <span>{email.label}</span>
            </a>
            <div className="flex items-start gap-3 text-primary-foreground/75">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0" />
              <span>Albuquerque, NM 87113, USA</span>
            </div>
          </div>
        </div>

        {/* Hours */}
        <div>
          <div className="mb-4 font-serif text-base text-accent">{t("contact.hours")}</div>
          <p className="text-sm text-primary-foreground/75">{t("contact.hoursVal")}</p>
        </div>
      </div>

      {/* NM Cities — SEO service area */}
      <div className="border-t border-primary-foreground/10">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
          <NMServiceArea variant="footer" />
        </div>
      </div>
      <div className="border-t border-primary-foreground/15">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-2 px-4 py-5 text-center text-xs text-primary-foreground/60 sm:flex-row sm:gap-4 sm:px-6 sm:text-left lg:px-8">
          <div suppressHydrationWarning>
            © <span suppressHydrationWarning>{new Date().getFullYear()}</span> AMR 3 Design &
            Construction LLC. {t("footer.rights")}
          </div>
          <div className="text-[11px] tracking-wide text-primary-foreground/60 shrink-0">
            {t("footer.poweredBy")}{" "}
            <a
              href="https://puulse.ai"
              target="_blank"
              rel="noopener noreferrer"
              className="font-serif font-semibold text-accent hover:underline"
            >
              Puulse AI
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
